import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { PulseStrip } from './AdminAnalytics/PulseStrip';
import { HeartbeatStrip } from './AdminAnalytics/HeartbeatStrip';
import { GrowthChart } from './AdminAnalytics/GrowthChart';
import { RevenueChart } from './AdminAnalytics/RevenueChart';
import { TopPerformers } from './AdminAnalytics/TopPerformers';
import { ActiveUsersCards } from './AdminAnalytics/ActiveUsersCards';
import { RetentionTable } from './AdminAnalytics/RetentionTable';
import { GeoSignupsMap } from './AdminAnalytics/GeoSignupsMap';
import { HostInvitesActivity } from './AdminAnalytics/HostInvitesActivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminOverviewPanel = ({ headers, onSelectVenue, onSelectHost, onSelectGuest, onNavigateTab }) => {
  const [range, setRange] = useState(30);
  const [pulse, setPulse] = useState(null);
  const [signups, setSignups] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [topPerformers, setTopPerformers] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [retention, setRetention] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pulseRes, signupsRes, revenueRes, topRes, activeRes, retentionRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/pulse`, { headers }),
        axios.get(`${API}/admin/analytics/signups-timeseries?days=${range}`, { headers }),
        axios.get(`${API}/admin/analytics/revenue-timeseries?days=${range}`, { headers }),
        axios.get(`${API}/admin/analytics/top-performers?days=${range}`, { headers }),
        axios.get(`${API}/admin/analytics/active-users`, { headers }),
        axios.get(`${API}/admin/analytics/retention?cohorts=6`, { headers }),
      ]);
      setPulse(pulseRes.data);
      setSignups(signupsRes.data);
      setRevenue(revenueRes.data);
      setTopPerformers(topRes.data);
      setActiveUsers(activeRes.data);
      setRetention(retentionRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('analytics load failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [headers, range]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRangeChange = useCallback((next) => {
    setRange(next);
  }, []);

  const sectionGap = useMemo(() => ({ marginTop: 12 }), []);

  // Build the last-14-days sparkline payload that PulseStrip overlays
  // on each metric row. Pulled from the existing timeseries calls so
  // there's no extra fetch — just a re-shape of the data the dashboard
  // already loads.
  const sparkData = useMemo(() => {
    const trimSignups = (signups || []).slice(-14);
    const trimRevenue = (revenue || []).slice(-14);
    return {
      guests: trimSignups.map((row) => row.guests || 0),
      hosts: trimSignups.map((row) => row.hosts || 0),
      venues: trimSignups.map((row) => row.venues || 0),
      bookings: trimRevenue.map((row) => row.transactions ?? row.bookings ?? 0),
      revenue: trimRevenue.map((row) => row.revenue || 0),
    };
  }, [signups, revenue]);

  return (
    <div data-testid="admin-overview-panel" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <HeartbeatStrip headers={headers} onNavigate={onNavigateTab} />
      <PulseStrip pulse={pulse} loading={loading} sparkData={sparkData} />

      <div style={sectionGap}>
        <GrowthChart data={signups} range={range} onRangeChange={handleRangeChange} loading={loading} />
      </div>

      <div style={sectionGap}>
        <RevenueChart data={revenue} loading={loading} />
      </div>

      <div style={sectionGap}>
        <ActiveUsersCards data={activeUsers} />
      </div>

      <div style={sectionGap}>
        <TopPerformers
          data={topPerformers}
          loading={loading}
          onClickVenue={onSelectVenue}
          onClickHost={onSelectHost}
          onClickGuest={onSelectGuest}
        />
      </div>

      <div style={sectionGap}>
        <HostInvitesActivity headers={headers} onSelectHost={onSelectHost} />
      </div>

      <div style={sectionGap}>
        <GeoSignupsMap headers={headers} />
      </div>

      <div style={sectionGap}>
        <RetentionTable data={retention} loading={loading} />
      </div>
    </div>
  );
};

export default AdminOverviewPanel;
